
import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Archive, RefreshCw } from "lucide-react";
import { Guard } from "../types";
import { useQuery } from "@tanstack/react-query";
import { getArchivedGuards } from "../services/guardService";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { restoreGuard } from "../services/guardService";

interface ArchivedGuardsDialogProps {
  onGuardRestored: () => void;
}

export const ArchivedGuardsDialog = ({ onGuardRestored }: ArchivedGuardsDialogProps) => {
  const [open, setOpen] = useState(false);
  const { user } = useSupabaseUser();
  const { toast } = useToast();
  const [isRestoring, setIsRestoring] = useState<string | null>(null);

  const { data: archivedGuards, isLoading, refetch } = useQuery({
    queryKey: ['archived-guards', user?.id],
    queryFn: async () => {
      if (!user) return [];
      return await getArchivedGuards();
    },
    enabled: !!user && open,
  });

  const handleRestoreGuard = async (guard: Guard) => {
    try {
      setIsRestoring(guard.id);
      
      // Use our service to restore the guard
      await restoreGuard(guard.id);
      
      // Show toast message
      const hasEmail = !!guard.email;
      
      toast({
        title: "Guard restored",
        description: hasEmail 
          ? `${guard.last_name} ${guard.first_name} has been restored to active status with login credentials. ${
              guard.email ? "A password reset email has been sent to their email address." : ""
            }`
          : `${guard.last_name} ${guard.first_name} has been restored to active status.`,
      });
      
      await refetch();
      onGuardRestored();
    } catch (error: any) {
      console.error('Error restoring guard:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to restore guard. Please try again.",
      });
    } finally {
      setIsRestoring(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <Archive className="h-4 w-4" />
          View Archive Personnel
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md md:max-w-lg lg:max-w-xl">
        <DialogHeader>
          <DialogTitle>Archived Guards</DialogTitle>
          <DialogDescription>
            View and restore guards that have been archived.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 max-h-[60vh] overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-8 w-8 border-2 border-primary rounded-full border-t-transparent"></div>
            </div>
          ) : archivedGuards?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No archived guards found.
            </div>
          ) : (
            <div className="space-y-4">
              {archivedGuards?.map((guard) => (
                <Card key={guard.id} className="p-4 flex justify-between items-center">
                  <div>
                    <div className="font-medium">{guard.last_name} {guard.first_name}</div>
                    <div className="text-sm text-muted-foreground">
                      <span>{guard.code || 'No code'}</span>
                      {guard.mobile_phone && <span> • {guard.mobile_phone}</span>}
                      {guard.email && <span> • {guard.email}</span>}
                    </div>
                    <div className="mt-1">
                      <Badge variant="outline" className="bg-amber-50">Archived</Badge>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="gap-2"
                    onClick={() => handleRestoreGuard(guard)}
                    disabled={isRestoring === guard.id}
                  >
                    {isRestoring === guard.id ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
                        Restoring...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4" />
                        Restore
                      </>
                    )}
                  </Button>
                </Card>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
